import { EMPTY_VALUE } from "staticConfiguration";
import LinearProgress from "@mui/material/LinearProgress";
import List, { type ListProps } from "@mui/material/List";
import { useFormatDate } from "core/hooks/useFormatDate";
import type {
  DocNodeSummaryWithParentsDto,
  WidgetTabValues,
} from "features/DMS/entities";
import { useLogDocumentView } from "features/DMS/hooks/useLogDocumentView";
import { useTranslation } from "react-i18next";
import { DMSWidgetFileListItem } from "./DMSWidgetDocumentFileListItem";
import { DMSWidgetLinkListItem } from "./DMSWidgetLinkListItem";

export interface DMSWidgetListProps extends ListProps {
  isLoading: boolean;
  documents: DocNodeSummaryWithParentsDto[];
  selectedTab: WidgetTabValues;
}

export function DMSWidgetList({
  documents,
  isLoading,
  selectedTab,
}: DMSWidgetListProps) {
  const { t } = useTranslation("DocumentManagementSystem", {
    keyPrefix: "Next:DocumentManagementSystem:DMSWidgetDocumentList",
  });
  const dateLabel = t(`dateLabel.${selectedTab}`);
  const formatDate = useFormatDate();
  const logDocumentView = useLogDocumentView();
  if (isLoading) {
    return <LinearProgress sx={{ mt: 2, display: "block" }} />;
  }
  return (
    <List disablePadding dense>
      {documents.map((document) => {
        const dateValue = document[selectedTab];
        const dateText = dateValue ? formatDate(dateValue) : EMPTY_VALUE;
        const parents =
          document.parents?.map((parent) => ({
            id: parent.id!,
            name: parent.name!,
            path: parent.path!,
            href:
              parent.type === "Library" || parent.type === "Folder"
                ? `/dms/library/v2${parent.path}`
                : null,
          })) ?? [];

        if (document.documentLink) {
          return (
            <DMSWidgetLinkListItem
              key={document.id}
              path={document.path!}
              isCorporateOnly={Boolean(document.isCorporateOnly)}
              onDocClick={logDocumentView}
              id={document.id!}
              title={document.name!}
              tags={document.tags ?? []}
              parents={parents}
              dateLabel={dateLabel}
              dateText={dateText}
              documentLink={document.documentLink!}
            />
          );
        }

        return (
          <DMSWidgetFileListItem
            key={document.id}
            path={document.path!}
            onDocClick={logDocumentView}
            id={document.id!}
            title={document.name!}
            isCorporateOnly={Boolean(document.isCorporateOnly)}
            tags={document.tags ?? []}
            parents={parents}
            fileId={document.fileId!}
            fileName={document.fileName!}
            dateLabel={dateLabel}
            dateText={dateText}
          />
        );
      })}
    </List>
  );
}
